import React from "react";
import {
    VStack,
    HStack,
    Text,
    Button,
    useToast,
    Alert,
    AlertIcon,
    Spinner,
    useDisclosure,
    Heading,
    Badge,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { EmailChangeModal, PhoneChangeModal } from "../authentication";
import { getColor } from "../../brandConfig";
import { UPDATE_CLIENT } from "./queries";
import { useProfileData, useFileUpload, useTenantData } from "./hooks";
import { ProfilePhotoUploader } from "./components/ProfilePhotoUploader";
import { PersonalInfoForm } from "./components/PersonalInfoForm";
import { cleanFormDataInput } from "./utils/formHelpers";

const EditProfile = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { user, refreshAuth } = useAuth();

    const textPrimary = getColor("text.primaryDark");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");
    const textInverse = getColor("text.inverse");
    const accentBlue = getColor("accentBlue");
    const cardBorder = getColor("border.darkCard");
    const primaryColor = getColor("primary");

    const { isOpen: isEmailModalOpen, onOpen: onEmailModalOpen, onClose: onEmailModalClose } = useDisclosure();
    const { isOpen: isSmsModalOpen, onOpen: onSmsModalOpen, onClose: onSmsModalClose } = useDisclosure();

    const { formData, setFormData, clientData, loading: queryLoading } = useProfileData(user?.id);
    const { tenantFormData, saveTenant } = useTenantData(user?.id);
    const { handlePhotoUpload } = useFileUpload();

    const [pendingChanges, setPendingChanges] = React.useState({
        email: "",
        phoneNumber: "",
    });

    const [updateClient, { loading: updateLoading }] = useMutation(UPDATE_CLIENT, {
        onCompleted: () => {
            toast({
                title: "Profile Updated",
                description: "Your profile has been updated successfully",
                status: "success",
                duration: 3000,
            });
            if (pendingChanges.email || pendingChanges.phoneNumber) {
                refreshAuth();
            }
            navigate("/profile");
        },
        onError: (error) => {
            toast({
                title: "Update Failed",
                description: error.message,
                status: "error",
                duration: 5000,
            });
        },
    });

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleEmailChange = () => {
        if (formData.email !== clientData?.email) {
            setPendingChanges((prev) => ({ ...prev, email: formData.email }));
            onEmailModalOpen();
        }
    };

    const handlePhoneChange = () => {
        if (formData.phoneNumber !== clientData?.phoneNumber) {
            setPendingChanges((prev) => ({ ...prev, phoneNumber: formData.phoneNumber }));
            onSmsModalOpen();
        }
    };

    const handleEmailVerificationSuccess = async () => {
        onEmailModalClose();
        toast({
            title: "Email Verified",
            description: "Your email has been verified and updated",
            status: "success",
            duration: 3000,
        });

        await updateClient({
            variables: {
                id: user?.id,
                input: cleanFormDataInput(formData, { email: pendingChanges.email }),
            },
        });
        setPendingChanges((prev) => ({ ...prev, email: "" }));
    };

    const handleSmsVerificationSuccess = async () => {
        onSmsModalClose();
        toast({
            title: "Phone Number Verified",
            description: "Your phone number has been verified and updated",
            status: "success",
            duration: 3000,
        });

        await updateClient({
            variables: {
                id: user?.id,
                input: cleanFormDataInput(formData, { phoneNumber: pendingChanges.phoneNumber }),
            },
        });
        setPendingChanges((prev) => ({ ...prev, phoneNumber: "" }));
    };

    const handleSave = async () => {
        if (!user?.id) return;

        const emailChanged = formData.email !== clientData?.email;
        const phoneChanged = formData.phoneNumber !== clientData?.phoneNumber;

        if (emailChanged) {
            handleEmailChange();
            return;
        }

        if (phoneChanged) {
            handlePhoneChange();
            return;
        }

        try {
            await updateClient({
                variables: {
                    id: user.id,
                    input: cleanFormDataInput(formData),
                },
            });

            if (tenantFormData.id) {
                await saveTenant();
            }
        } catch (error) {
            console.error("Error updating profile:", error);
        }
    };

    const onPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPhotoUrl = await handlePhotoUpload(e);
        if (newPhotoUrl) {
            setFormData((prev) => ({ ...prev, profilePhoto: newPhotoUrl }));
        }
    };

    if (queryLoading) {
        return (
            <VStack spacing={8}>
                <Spinner size="xl" />
                <Text>Loading profile details...</Text>
            </VStack>
        );
    }

    const emailChanged = formData.email !== clientData?.email;
    const phoneChanged = formData.phoneNumber !== clientData?.phoneNumber;

    return (
        <>
            <VStack spacing={8} align="stretch">
                <HStack justify="space-between">
                    <VStack align="start" spacing={2}>
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/profile")}
                            leftIcon={<ArrowBackIcon />}
                        >
                            Back to Profile
                        </Button>
                        <HStack spacing={3} align="center">
                            <Heading size="lg" color={textInverse}>
                                ✏️ Edit Profile
                            </Heading>
                            <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">
                                CUSTOMER
                            </Badge>
                        </HStack>
                        <Text color={textSecondary}>Update your personal and business information</Text>
                        {user?.id && (
                            <Text fontSize="sm" color={textMuted}>
                                Client ID:{" "}
                                <Text as="span" fontFamily="mono" color={textSecondary}>
                                    {user.id}
                                </Text>
                            </Text>
                        )}
                    </VStack>
                </HStack>

                {(emailChanged || phoneChanged) && (
                    <Alert status="warning">
                        <AlertIcon />
                        <VStack align="start" spacing={1}>
                            <Text fontWeight="bold">Verification Required</Text>
                            <Text>
                                {emailChanged && "Email changes require verification via email. "}
                                {phoneChanged && "Phone number changes require SMS verification. "}
                                Click "Save Changes" to start the verification process.
                            </Text>
                        </VStack>
                    </Alert>
                )}

                <ProfilePhotoUploader
                    profilePhoto={formData.profilePhoto}
                    fName={formData.fName}
                    lName={formData.lName}
                    onPhotoUpload={onPhotoUpload}
                />

                <PersonalInfoForm
                    formData={formData}
                    emailChanged={emailChanged}
                    phoneChanged={phoneChanged}
                    onChange={handleInputChange}
                />

                <HStack justify="flex-end" spacing={4}>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/profile")}
                        borderColor={cardBorder}
                        color={textSecondary}
                        _hover={{ borderColor: primaryColor, color: textInverse }}
                    >
                        Cancel
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={handleSave}
                        isLoading={updateLoading}
                        loadingText="Saving..."
                        bg={primaryColor}
                        _hover={{ bg: accentBlue }}
                    >
                        Save Changes
                    </Button>
                </HStack>
            </VStack>

            <EmailChangeModal
                isOpen={isEmailModalOpen}
                onClose={() => {
                    onEmailModalClose();
                    setFormData((prev) => ({ ...prev, email: clientData?.email || "" }));
                    setPendingChanges((prev) => ({ ...prev, email: "" }));
                }}
                clientId={user?.id || ""}
                newEmail={pendingChanges.email || formData.email}
                onSuccess={handleEmailVerificationSuccess}
            />

            <PhoneChangeModal
                isOpen={isSmsModalOpen}
                onClose={() => {
                    onSmsModalClose();
                    setFormData((prev) => ({ ...prev, phoneNumber: clientData?.phoneNumber || "" }));
                    setPendingChanges((prev) => ({ ...prev, phoneNumber: "" }));
                }}
                clientId={user?.id || ""}
                newPhoneNumber={pendingChanges.phoneNumber || formData.phoneNumber}
                onSuccess={handleSmsVerificationSuccess}
            />
        </>
    );
};

export default EditProfile;
